import { isFulfilled, isPending, isRejected, Middleware } from '@reduxjs/toolkit';
import runtimeConfig from 'config/runtimeConfig';
import { recordTrace } from 'services/firebase';

const MAX_TRACE_NAME_LENGTH = 100;

// Separa los entornos en la consola de Firebase: comparten appId, así que sin este
// atributo pre, EU y US se mezclan. Sale de la config en tiempo de ejecución, no hardcodeado.
const appHost = (() => {
  try {
    return new URL(runtimeConfig.APP_URL).hostname;
  } catch {
    return 'unknown';
  }
})();

type TraceResult = 'ok' | 'error' | 'logic_error';

// tmc-gdp-api responde HTTP 200 con `success: false` en los fallos lógicos, así que un
// thunk `fulfilled` no implica que la operación saliera bien.
const resultOf = (action: any): TraceResult => {
  if (isRejected(action)) return 'error';
  return action.payload?.data?.success === false ? 'logic_error' : 'ok';
};

const pendingStarts = new Map<string, { epochMs: number; perfMs: number }>();

/**
 * Mide cada thunk (createAsyncThunk) desde `pending` hasta `fulfilled`/`rejected` y lo
 * registra como traza personalizada de Firebase Performance con el nombre del thunk
 * (p. ej. `patients/getPatients`). Incluye la petición y el procesado del resultado,
 * es decir, la espera que percibe el usuario.
 *
 * Atributos: `result` (ok | error | logic_error) y `app_host` (entorno).
 */
export const thunkTraceMiddleware: Middleware = () => (next) => (action) => {
  const requestId: string | undefined = (action as any)?.meta?.requestId;

  if (requestId && isPending(action)) {
    pendingStarts.set(requestId, { epochMs: Date.now(), perfMs: performance.now() });
  } else if (requestId && (isFulfilled(action) || isRejected(action))) {
    const start = pendingStarts.get(requestId);
    pendingStarts.delete(requestId);
    // Un thunk abortado (petición sustituida o componente desmontado) no es una medida real.
    if (start && !(action.meta as any)?.aborted) {
      const name = action.type.replace(/\/(fulfilled|rejected)$/, '').slice(0, MAX_TRACE_NAME_LENGTH);
      recordTrace(name, start.epochMs, performance.now() - start.perfMs, {
        result: resultOf(action),
        app_host: appHost,
      });
    }
  }

  return next(action);
};
